import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { FullQuestionPOJO } from "../../../../models/Survey";
import EAAlert from "../../../shared/EAAlert";
import EAButton from "../../../shared/Buttons/EAButton";
import EASkeletonLoader from "../../../shared/EASkeletonLoader";
import { GET_LINKED_ACCOUNTS_URL } from "../../../../api/apiRoutes";
import { LinkedAccount } from "../../../../models/LinkedAccount";
import { ADD_SUBACCOUNT_PAGE, LOGIN_PAGE } from "../../../../routes";
import { State } from "../../../../store";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import { mergeSearchWithTab } from "../../../../../utils/myAccountTabUrl";
import SurveyAccountOption from "./SurveyAccountOption";
import SurveyAccountAddOption from "./SurveyAccountAddOption";

export interface AccountSelectProps {
  question: FullQuestionPOJO;
  qid: string;
  value: string;
  onChange: (val: string) => void;
  hasError: boolean;
  helperText: string;
}

const getAccountId = (account: LinkedAccount): string => `${account.objectDb ?? 0},${account.objectId ?? 0}`;

const AccountSelect: React.FC<AccountSelectProps> = ({ question, qid, value, onChange, hasError, helperText }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const isLoggedIn = useSelector((state: State) => state.auth.isLoggedIn);
  const allowSubaccounts = useSelector(
    (state: State) => state.config.configuration.allowSubaccounts === true
  );
  const [accounts, setAccounts] = useState<LinkedAccount[]>([]);
  const [fetchState, setFetchState] = useState<"idle" | "loading" | "done">(() => (isLoggedIn ? "loading" : "idle"));

  useEffect(() => {
    if (!isLoggedIn) {
      setAccounts([]);
      setFetchState("idle");
      return;
    }

    let cancelled = false;

    const fetchAccounts = async () => {
      setFetchState("loading");
      setAccounts([]);
      const { result, isError } = await get(GET_LINKED_ACCOUNTS_URL(facilityIds));
      if (cancelled) {
        return;
      }
      if (!isError && Array.isArray(result)) {
        setAccounts(result as LinkedAccount[]);
      } else {
        setAccounts([]);
      }
      setFetchState("done");
    };

    fetchAccounts();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityIds, isLoggedIn]);

  useEffect(() => {
    if (fetchState !== "done" || accounts.length !== 1 || value.trim()) {
      return;
    }
    onChange(getAccountId(accounts[0]!));
  }, [fetchState, accounts, value, onChange]);

  const isLoading = fetchState === "loading";
  const isEmpty = fetchState === "done" && accounts.length === 0;
  const showValidationError = hasError && !value.trim();

  const myAccountPeopleUrl = `${LOGIN_PAGE}?${mergeSearchWithTab(location.search, "people")}`;
  const addSubaccountUrl = `${ADD_SUBACCOUNT_PAGE}?${mergeSearchWithTab(location.search, "people")}`;

  const handleAddParticipant = () => {
    navigate(addSubaccountUrl);
  };

  const getDisplayHelperText = (): string | null => {
    if (showValidationError) {
      if (isEmpty) {
        return t("survey_account_validation_empty");
      }
      if (accounts.length > 0) {
        return t("survey_account_validation_required");
      }
    }
    if (isLoading) {
      return t("survey_account_loading");
    }
    return helperText || null;
  };

  const displayHelperText = getDisplayHelperText();
  const helperTextClassName = `custom-text-field__helper-text ${showValidationError || hasError ? "error" : ""}`;

  const myAccountLink = (
    <p className="survey-account-select__caption">
      <Trans
        i18nKey="survey_account_my_account_caption"
        components={{
          1: <Link to={myAccountPeopleUrl} className="survey-account-select__caption-link" />,
        }}
      />
    </p>
  );

  const renderHelperText = () =>
    displayHelperText ? <p className={helperTextClassName}>{displayHelperText}</p> : null;

  if (!isLoggedIn) {
    return (
      <div className="survey-account-select survey-account-select--gate">
        <p className="custom-text-field__label" id={qid}>
          {question.question}
        </p>
        <EAAlert
          type="warning"
          message={t("survey_account_login_required")}
          buttonText={t("zaloguj_sie")}
          onButtonClick={() => navigate(`${LOGIN_PAGE}?redirect=previousPath`)}
        />
        {renderHelperText()}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="survey-account-select">
        <p className="custom-text-field__label" id={qid}>
          {question.question}
        </p>
        <EASkeletonLoader height="72px" />
        {renderHelperText()}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div
        className={`survey-account-select survey-account-select--gate ${showValidationError ? "survey-account-select--gate-error" : ""}`}
      >
        <p className="custom-text-field__label" id={qid}>
          {question.question}
        </p>
        <p className="survey-account-select__message">{t("survey_account_empty_hint")}</p>
        <div className="survey-account-select__empty-actions">
          {allowSubaccounts ? (
            <EAButton title={t("survey_account_add_person")} variant="filled" onClick={handleAddParticipant} />
          ) : null}
          {myAccountLink}
        </div>
        {renderHelperText()}
      </div>
    );
  }

  return (
    <div className="survey-account-select">
      <p className="custom-text-field__label" id={qid}>
        {question.question}
      </p>
      <div className={`survey-account-select__list ${showValidationError ? "survey-account-select__list--error" : ""}`}>
        <div role="radiogroup" aria-labelledby={qid} className="survey-account-select__options">
          {accounts.map((account, index) => {
            const accountId = getAccountId(account);
            return (
              <SurveyAccountOption
                key={`${accountId}-${index}`}
                name={qid}
                account={account}
                accountId={accountId}
                checked={value === accountId}
                isCurrentUser={index === 0}
                onChange={onChange}
              />
            );
          })}
        </div>
        {allowSubaccounts ? <SurveyAccountAddOption onClick={handleAddParticipant} /> : null}
      </div>
      {renderHelperText()}
    </div>
  );
};

export default AccountSelect;
